import axios from "axios";

// ===============================
// BASE URL — env-la irundhu varum
// Local .env          : REACT_APP_API_URL=http://localhost:5000
// Production build/env: REACT_APP_API_URL=https://back.mithralaya.in
// (env maathina REBUILD + REDEPLOY kattayam — React build time-la dhaan env padikkum)
// ===============================
const getBaseURL = () => {
  const configuredUrl = (
    process.env.REACT_APP_API_URL || "http://localhost:5001"
  ).trim();

  const trimmedUrl = configuredUrl.replace(/\/+$/, "");

  return trimmedUrl.endsWith("/api")
    ? trimmedUrl
    : `${trimmedUrl}/api`;
};

const api = axios.create({
  baseURL: getBaseURL(),
  withCredentials: true,
});

// ===============================
// REQUEST INTERCEPTOR
// ===============================
api.interceptors.request.use(
  (config) => {
    const isFormData =
      typeof FormData !== "undefined" && config.data instanceof FormData;

    // FormData must keep the browser-generated multipart boundary.
    // A global application/json header made /upload look empty to multer.
    if (isFormData) {
      if (config.headers && typeof config.headers.delete === "function") {
        config.headers.delete("Content-Type");
      } else if (config.headers) {
        delete config.headers["Content-Type"];
      }
    } else if (config.data != null) {
      const headers = config.headers;
      const current =
        headers && typeof headers.get === "function"
          ? headers.get("Content-Type")
          : headers?.["Content-Type"];
      if (!current && headers && typeof headers.set === "function") {
        headers.set("Content-Type", "application/json");
      } else if (!current && headers) {
        headers["Content-Type"] = "application/json";
      }
    }

    // AuthContext saves the token as "mmw_token". This used to read "token",
    // so no Authorization header was ever sent and requests depended on the
    // cookie alone — which mobile browsers often block, giving
    // "Not authorized, no token" on Add to cart.
    const token = localStorage.getItem("mmw_token") || localStorage.getItem("token");

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Marks every call as coming from this app. Other websites can't add
    // this header, so the backend uses it to reject forged requests
    // (Mithralaya-server/middleware/security.js → requireAppHeader).
    config.headers["X-Requested-With"] = "XMLHttpRequest";

    return config;
  },
  (error) => Promise.reject(error)
);

// ===============================
// RESPONSE INTERCEPTOR
// ===============================
// Access tokens are short-lived. When one expires, the httpOnly refresh cookie
// buys a new one and the original request is retried, so customers stay signed
// in. Parallel 401s share a single refresh call (each refresh token works once).
let refreshing = null;

const refreshAccessToken = () => {
  if (!refreshing) {
    refreshing = axios
      .post(`${getBaseURL()}/auth/refresh`, null, {
        withCredentials: true,
        headers: { "X-Requested-With": "XMLHttpRequest" },
      })
      .then(({ data }) => {
        localStorage.setItem("mmw_token", data.token);
        return data.token;
      })
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
};

// A 401 from these means wrong credentials, not an expired session
const NO_REFRESH = /\/auth\/(login|register|google|phone\/|refresh|logout)/;

api.interceptors.response.use(
  (response) => response,

  async (error) => {
    // Backend not running / unreachable
    if (error.code === "ERR_NETWORK") {
      console.warn("Backend not reachable.");
      return Promise.reject(error);
    }

    const original = error.config;
    if (
      error.response?.status === 401 &&
      original &&
      !original._retried &&
      !NO_REFRESH.test(original.url || "") &&
      localStorage.getItem("mmw_token")
    ) {
      original._retried = true;
      try {
        const token = await refreshAccessToken();
        original.headers.Authorization = `Bearer ${token}`;
        return await api(original);
      } catch (retryError) {
        // the retry itself failed for its own reason (e.g. a 400): report that
        if (retryError.config?.url && !/\/auth\/refresh/.test(retryError.config.url) && retryError.response?.status !== 401) {
          return Promise.reject(retryError);
        }
      }
    }

    // Unauthorized
    if (error.response?.status === 401) {
      console.warn("Unauthorized. Please login again.");

      const hadSession = Boolean(localStorage.getItem("mmw_token"));
      localStorage.removeItem("mmw_token");
      localStorage.removeItem("mmw_user");
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      // Let AuthContext log the user out so the UI doesn't look signed in
      if (hadSession) window.dispatchEvent(new Event("auth:expired"));

      // Uncomment if you want auto redirect
      // window.location.href = "/login";
    }

    // Forbidden
    if (error.response?.status === 403) {
      console.warn("Admin access required.");
    }

    return Promise.reject(error);
  }
);

export default api;